import { useState, useEffect } from 'react';
import { 
  Users, DollarSign, Package, TrendingUp, Settings, 
  Shield, Ban, Trash2, Plus, Eye, EyeOff, Gift, Loader2,
  Edit3, Check, X, ChevronDown, Sparkles, Tag, Target, Calendar, Clock,
  BarChart3, Zap, AlertTriangle, Search
} from 'lucide-react';
import { SEO } from '../components/SEO';
import { API_URL, publicAnonKey } from '../utils/supabaseClient';
import { getSupabaseClient } from '../utils/supabaseClient';
import { toast } from 'sonner';
import { motion } from 'motion/react';
import { useAuth } from '../contexts/AuthContext';
import { PromoCodeForm } from '../components/admin/PromoCodeForm';

const supabase = getSupabaseClient();

interface User {
  id: string;
  email: string;
  name?: string;
  credits: number;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  isBanned?: boolean;
  emailVerified?: boolean;
  createdAt: string;
}

interface UserRestrictions {
  type: 'all' | 'new_users' | 'inactive_users' | 'verified_only' | 'custom';
  newUserDays?: number;
  inactiveDays?: number;
  minProjects?: number;
  maxProjects?: number;
  emailVerified?: boolean;
  neverPurchased?: boolean;
  registeredAfter?: string;
  registeredBefore?: string;
}

interface PromoCode {
  code: string;
  credits: number;
  description: string;
  isActive: boolean;
  
  // Type & Category
  type: 'public' | 'targeted' | 'unique' | 'recurring';
  category: 'marketing' | 'retention' | 'acquisition' | 'loyalty';
  preset?: string;
  
  // Time Restrictions
  startsAt: string | null;
  expiresAt: string | null;
  validForDays: number | null;
  
  // Usage Limits
  maxUses: number | null;
  maxUsesPerUser: number | null;
  dailyLimit: number | null;
  usedCount: number;
  
  // User Restrictions
  userRestrictions: UserRestrictions;
  
  // Recurring Settings
  isRecurring: boolean;
  recurringInterval?: 'daily' | 'weekly' | 'monthly';
  recurringCount?: number;
  
  // Campaign Tracking
  campaignName?: string;
  campaignId?: string;
  trackingEnabled: boolean;
  tags: string[];
  
  // Metadata
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  lastUsedAt: string | null;
  
  // Analytics
  uniqueUsers: number;
  totalCreditsGiven: number;
}

interface Stats {
  totalUsers: number;
  verifiedUsers: number;
  totalCredits: number;
}

async function createAuthHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${publicAnonKey}`,
    'X-Auth-Token': token,
  };
}

export function Admin() {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'promo' | 'reconcile'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingCredit, setEditingCredit] = useState<string | null>(null);
  const [creditAmount, setCreditAmount] = useState<string>('');
  
  // Promo code form
  const [newPromoCode, setNewPromoCode] = useState('');
  const [newPromoCredits, setNewPromoCredits] = useState('');
  const [newPromoDescription, setNewPromoDescription] = useState('');
  const [newPromoMaxUses, setNewPromoMaxUses] = useState('');
  const [newPromoExpiresAt, setNewPromoExpiresAt] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [presets, setPresets] = useState<any>({});
  
  // Advanced options
  const [newPromoType, setNewPromoType] = useState<'public' | 'targeted' | 'unique' | 'recurring'>('public');
  const [newPromoCategory, setNewPromoCategory] = useState<'marketing' | 'retention' | 'acquisition' | 'loyalty'>('marketing');
  const [newPromoMaxUsesPerUser, setNewPromoMaxUsesPerUser] = useState('');
  const [newPromoDailyLimit, setNewPromoDailyLimit] = useState('');
  const [newPromoStartsAt, setNewPromoStartsAt] = useState('');
  const [newPromoCampaignName, setNewPromoCampaignName] = useState('');
  
  // User restrictions
  const [restrictionType, setRestrictionType] = useState<'all' | 'new_users' | 'inactive_users' | 'verified_only' | 'custom'>('all');
  const [newUserDays, setNewUserDays] = useState('7');
  const [inactiveDays, setInactiveDays] = useState('60');
  const [minProjects, setMinProjects] = useState('');
  const [maxProjects, setMaxProjects] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [neverPurchased, setNeverPurchased] = useState(false);
  
  // Recurring settings
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [recurringCount, setRecurringCount] = useState('');
  
  // Reconciliation state
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileReport, setReconcileReport] = useState<any>(null);
  const [reconcileUserId, setReconcileUserId] = useState('');
  const [autoFix, setAutoFix] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Initialize: Refresh user to ensure admin status is loaded
  useEffect(() => {
    const initAdmin = async () => {
      await refreshUser();
    };
    initAdmin();
  }, []);

  // Load presets on mount
  useEffect(() => {
    loadPresets();
  }, []);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadPresets = async () => {
    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_URL}/admin/promo-codes/presets`, { headers });
      if (response.ok) {
        const data = await response.json();
        setPresets(data.presets || {});
      }
    } catch (error) {
      console.error('Failed to load presets:', error);
    }
  };

  const applyPreset = (presetKey: string) => {
    const preset = presets[presetKey];
    if (!preset) return;

    setNewPromoCredits(preset.credits?.toString() || '');
    setNewPromoDescription(preset.description || '');
    setNewPromoType(preset.type || 'public');
    setNewPromoCategory(preset.category || 'marketing');
    setNewPromoMaxUses(preset.maxUses?.toString() || '');
    setNewPromoMaxUsesPerUser(preset.maxUsesPerUser?.toString() || '');
    setRestrictionType(preset.userRestrictions?.type || 'all');
    setIsRecurring(preset.isRecurring || false);
    setRecurringInterval(preset.recurringInterval || 'monthly');
    setRecurringCount(preset.recurringCount?.toString() || '');

    if (preset.userRestrictions) {
      setNewUserDays(preset.userRestrictions.newUserDays?.toString() || '7');
      setInactiveDays(preset.userRestrictions.inactiveDays?.toString() || '60');
      setMinProjects(preset.userRestrictions.minProjects?.toString() || '');
      setMaxProjects(preset.userRestrictions.maxProjects?.toString() || '');
      setEmailVerified(preset.userRestrictions.emailVerified || false);
      setNeverPurchased(preset.userRestrictions.neverPurchased || false);
    }

    setShowAdvanced(true);
  };

  const resetForm = () => {
    setNewPromoCode('');
    setNewPromoCredits('');
    setNewPromoDescription('');
    setNewPromoMaxUses('');
    setNewPromoExpiresAt('');
    setSelectedPreset('');
    setShowAdvanced(false);
    setNewPromoType('public');
    setNewPromoCategory('marketing');
    setNewPromoMaxUsesPerUser('');
    setNewPromoDailyLimit('');
    setNewPromoStartsAt('');
    setNewPromoCampaignName('');
    setRestrictionType('all');
    setNewUserDays('7');
    setInactiveDays('60');
    setMinProjects('');
    setMaxProjects('');
    setEmailVerified(false);
    setNeverPurchased(false);
    setIsRecurring(false);
    setRecurringInterval('monthly');
    setRecurringCount('');
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = await createAuthHeaders();

      if (activeTab === 'users') {
        console.log('[ADMIN] Fetching users and stats...');
        const [usersRes, statsRes] = await Promise.all([
          fetch(`${API_URL}/admin/users`, { headers }),
          fetch(`${API_URL}/admin/stats`, { headers })
        ]);

        console.log('[ADMIN] Users response:', usersRes.status, usersRes.statusText);
        console.log('[ADMIN] Stats response:', statsRes.status, statsRes.statusText);

        if (!usersRes.ok || !statsRes.ok) {
          const usersError = !usersRes.ok ? await usersRes.json() : null;
          const statsError = !statsRes.ok ? await statsRes.json() : null;
          console.error('[ADMIN] Users error:', usersError);
          console.error('[ADMIN] Stats error:', statsError);
          throw new Error(usersError?.message || statsError?.message || 'Failed to load admin data');
        }

        const usersData = await usersRes.json();
        const statsData = await statsRes.json();

        console.log('[ADMIN] Users data:', usersData);
        console.log('[ADMIN] Stats data:', statsData);

        setUsers(usersData.users || []);
        setStats(statsData);
      } else if (activeTab === 'reconcile') {
        // Also load users for reconcile tab so they can select from dropdown
        console.log('[ADMIN] Fetching users for reconciliation...');
        const usersRes = await fetch(`${API_URL}/admin/users`, { headers });
        
        if (!usersRes.ok) {
          const error = await usersRes.json();
          console.error('[ADMIN] Users error:', error);
          throw new Error(error.message || 'Failed to load users');
        }
        
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
      } else {
        console.log('[ADMIN] Fetching promo codes...');
        const promoRes = await fetch(`${API_URL}/admin/promo-codes`, { headers });
        console.log('[ADMIN] Promo response:', promoRes.status, promoRes.statusText);
        
        if (!promoRes.ok) {
          const error = await promoRes.json();
          console.error('[ADMIN] Promo error:', error);
          throw new Error(error.message || 'Failed to load promo codes');
        }
        
        const promoData = await promoRes.json();
        console.log('[ADMIN] Promo data:', promoData);
        setPromoCodes(promoData.promoCodes || []);
      }
    } catch (error) {
      console.error('[ADMIN] Failed to load admin data:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCredits = async (userId: string) => {
    try {
      const credits = parseInt(creditAmount);
      if (isNaN(credits)) {
        toast.error('Invalid credit amount');
        return;
      }

      const headers = await createAuthHeaders();
      const response = await fetch(`${API_URL}/admin/users/${userId}/add-credits`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ credits }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update credits');
      }

      const result = await response.json();
      toast.success(result.message);
      
      setEditingCredit(null);
      setCreditAmount('');
      loadData();
    } catch (error) {
      console.error('Failed to update credits:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update credits');
    }
  };

  const handleToggleBan = async (userId: string) => {
    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_URL}/admin/users/${userId}/ban`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: 'Admin action' }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to ban user');
      }

      const result = await response.json();
      toast.success(result.message);
      loadData();
    } catch (error) {
      console.error('Failed to ban user:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to ban user');
    }
  };

  const handleToggleAdmin = async (userId: string) => {
    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_URL}/admin/users/${userId}/toggle-admin`, {
        method: 'POST',
        headers,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to toggle admin status');
      }

      const result = await response.json();
      toast.success(result.message);
      loadData();
    } catch (error) {
      console.error('Failed to toggle admin:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to toggle admin status');
    }
  };

  const handleCreatePromo = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const credits = parseInt(newPromoCredits);
      const maxUses = newPromoMaxUses ? parseInt(newPromoMaxUses) : null;
      const maxUsesPerUser = newPromoMaxUsesPerUser ? parseInt(newPromoMaxUsesPerUser) : null;
      const dailyLimit = newPromoDailyLimit ? parseInt(newPromoDailyLimit) : null;
      const recurringCountNum = recurringCount ? parseInt(recurringCount) : null;
      
      if (!newPromoCode || isNaN(credits) || credits <= 0) {
        toast.error('Invalid promo code data');
        return;
      }

      // Build user restrictions
      const userRestrictions: UserRestrictions = {
        type: restrictionType,
      };

      if (restrictionType === 'new_users' || restrictionType === 'custom') {
        if (newUserDays) userRestrictions.newUserDays = parseInt(newUserDays);
      }
      if (restrictionType === 'inactive_users' || restrictionType === 'custom') {
        if (inactiveDays) userRestrictions.inactiveDays = parseInt(inactiveDays);
      }
      if (restrictionType === 'custom') {
        if (minProjects) userRestrictions.minProjects = parseInt(minProjects);
        if (maxProjects) userRestrictions.maxProjects = parseInt(maxProjects);
        if (emailVerified) userRestrictions.emailVerified = emailVerified;
        if (neverPurchased) userRestrictions.neverPurchased = neverPurchased;
      }
      if (restrictionType === 'verified_only') {
        userRestrictions.emailVerified = true;
      }

      const headers = await createAuthHeaders();
      const response = await fetch(`${API_URL}/admin/promo-codes`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          code: newPromoCode,
          credits,
          description: newPromoDescription,
          preset: selectedPreset || undefined,
          type: newPromoType,
          category: newPromoCategory,
          startsAt: newPromoStartsAt || null,
          expiresAt: newPromoExpiresAt || null,
          maxUses,
          maxUsesPerUser,
          dailyLimit,
          userRestrictions,
          isRecurring,
          recurringInterval: isRecurring ? recurringInterval : undefined,
          recurringCount: isRecurring ? recurringCountNum : undefined,
          campaignName: newPromoCampaignName || undefined,
          trackingEnabled: true,
          tags: [],
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create promo code');
      }

      const result = await response.json();
      toast.success(result.message);
      
      // Reset form
      resetForm();
      
      loadData();
    } catch (error) {
      console.error('Failed to create promo code:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create promo code');
    }
  };

  const handleTogglePromo = async (code: string) => {
    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_URL}/admin/promo-codes/${code}/toggle`, {
        method: 'POST',
        headers,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to toggle promo code');
      }

      const result = await response.json();
      toast.success(result.message);
      loadData();
    } catch (error) {
      console.error('Failed to toggle promo code:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to toggle promo code');
    }
  };

  const handleDeletePromo = async (code: string) => {
    if (!confirm(`Are you sure you want to delete promo code "${code}"?`)) {
      return;
    }

    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_URL}/admin/promo-codes/${code}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete promo code');
      }

      const result = await response.json();
      toast.success(result.message);
      loadData();
    } catch (error) {
      console.error('Failed to delete promo code:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete promo code');
    }
  };

  const handleReconcile = async () => {
    try {
      setReconcileLoading(true);
      setReconcileReport(null);
      
      const headers = await createAuthHeaders();
      const params = new URLSearchParams();
      if (reconcileUserId) params.append('userId', reconcileUserId);
      params.append('autoFix', autoFix.toString());
      params.append('limit', '100');
      
      const response = await fetch(`${API_URL}/admin/reconcile-credits?${params}`, {
        headers,
      });

      if (!response.ok) {
        let errorMessage = 'Reconciliation failed';
        try {
          const error = await response.json();
          errorMessage = error.error || errorMessage;
        } catch {
          // If JSON parse fails, try to get text
          const text = await response.text();
          errorMessage = text || `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setReconcileReport(result.report);
      
      if (result.summary.discrepanciesFound > 0) {
        if (autoFix) {
          toast.success(`Fixed ${result.summary.discrepanciesFixed} discrepancies! Added ${result.summary.totalCreditsAdded} credits.`);
        } else {
          toast.warning(`Found ${result.summary.discrepanciesFound} discrepancies. Enable Auto-Fix to correct them.`);
        }
      } else {
        toast.success('All credits are accurate! No discrepancies found.');
      }
      
      // Refresh data if we fixed anything
      if (autoFix && result.summary.fixedDiscrepancies > 0) {
        loadData();
      }
    } catch (error) {
      console.error('Reconciliation failed:', error);
      toast.error(error instanceof Error ? error.message : 'Reconciliation failed');
    } finally {
      setReconcileLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Admin Panel - HelioROI"
        description="Manage users and platform settings"
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-red-600" />
            <h1 className="text-3xl font-bold text-slate-900">Admin Panel</h1>
          </div>
          <p className="text-slate-600">
            Manage users, credits, and promo codes
          </p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-700 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-sm font-medium">Total Users</span>
              </div>
              <p className="text-2xl font-bold text-blue-900">{stats.totalUsers}</p>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-700 mb-1">
                <Package className="w-4 h-4" />
                <span className="text-sm font-medium">Total Credits</span>
              </div>
              <p className="text-2xl font-bold text-green-900">{stats.totalCredits.toLocaleString()}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'users'
                ? 'text-emerald-600 border-b-2 border-emerald-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Users
          </button>
          <button
            onClick={() => setActiveTab('promo')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'promo'
                ? 'text-emerald-600 border-b-2 border-emerald-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Gift className="w-4 h-4 inline mr-2" />
            Promo Codes
          </button>
          <button
            onClick={() => setActiveTab('reconcile')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'reconcile'
                ? 'text-orange-600 border-b-2 border-orange-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            Credit Reconciliation
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
        )}

        {/* Users Tab */}
        {!loading && activeTab === 'users' && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Credits</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {users.map((user) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-slate-50"
                    >
                      <td className="px-6 py-4 text-sm text-slate-900">{user.email}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{user.name || '—'}</td>
                      <td className="px-6 py-4 text-sm">
                        {editingCredit === user.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={creditAmount}
                              onChange={(e) => setCreditAmount(e.target.value)}
                              placeholder="±Credits"
                              className="w-24 px-2 py-1 border border-slate-300 rounded text-sm"
                              autoFocus
                            />
                            <button
                              onClick={() => handleEditCredits(user.id)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingCredit(null);
                                setCreditAmount('');
                              }}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{user.credits}</span>
                            <button
                              onClick={() => {
                                setEditingCredit(user.id);
                                setCreditAmount('');
                              }}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit Credits"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-1">
                          {user.isSuperAdmin && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-medium">
                              Super Admin
                            </span>
                          )}
                          {user.isAdmin && !user.isSuperAdmin && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-medium">
                              Admin
                            </span>
                          )}
                          {user.isBanned && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-medium">
                              Banned
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right text-sm">
                        <div className="flex items-center justify-end gap-2">
                          {!user.isSuperAdmin && (
                            <>
                              <button
                                onClick={() => handleToggleAdmin(user.id)}
                                className={`px-3 py-1.5 rounded-lg transition-colors ${
                                  user.isAdmin
                                    ? 'bg-amber-600 hover:bg-amber-700 text-white'
                                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                                }`}
                                title={user.isAdmin ? 'Revoke admin privileges' : 'Grant admin privileges'}
                              >
                                <Shield className="w-4 h-4 inline mr-1" />
                                {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                              </button>
                              <button
                                onClick={() => handleToggleBan(user.id)}
                                className={`px-3 py-1.5 rounded-lg transition-colors ${
                                  user.isBanned
                                    ? 'bg-green-600 hover:bg-green-700 text-white'
                                    : 'bg-red-600 hover:bg-red-700 text-white'
                                }`}
                              >
                                <Ban className="w-4 h-4 inline mr-1" />
                                {user.isBanned ? 'Unban' : 'Ban'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Credit Reconciliation Tab */}
        {!loading && activeTab === 'reconcile' && (
          <div className="space-y-6">
            {/* Reconciliation Control Panel */}
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-xl p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-orange-900 mb-2">
                    Credit Reconciliation System
                  </h3>
                  <p className="text-orange-800 mb-4">
                    Stripe is the <strong>source of truth</strong>. This tool compares Stripe payment records with our database and fixes any discrepancies.
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-orange-200">
                    <p className="text-sm text-slate-700 mb-2"><strong>Common Issues Fixed:</strong></p>
                    <ul className="text-sm text-slate-600 space-y-1 ml-4">
                      <li>• Webhook never fired (network issue, Stripe downtime)</li>
                      <li>• Webhook fired but failed processing (bug, timeout)</li>
                      <li>• Race conditions causing missing credits</li>
                      <li>• Promo codes incorrectly affecting credit amounts</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Select User (optional - leave empty for all users)
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={userSearchQuery}
                      onChange={(e) => {
                        setUserSearchQuery(e.target.value);
                        setShowUserDropdown(true);
                      }}
                      onFocus={() => setShowUserDropdown(true)}
                      onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
                      placeholder="Search by email or name..."
                      className="w-full pl-10 pr-10 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    {reconcileUserId && (
                      <button
                        onClick={() => {
                          setReconcileUserId('');
                          setUserSearchQuery('');
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Selected User Display */}
                  {reconcileUserId && !showUserDropdown && (
                    <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="text-sm">
                          <div className="font-medium text-orange-900">
                            {users.find(u => u.id === reconcileUserId)?.email || reconcileUserId}
                          </div>
                          <div className="text-xs text-orange-600">
                            {users.find(u => u.id === reconcileUserId)?.name || 'Selected User'}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setReconcileUserId('');
                            setUserSearchQuery('');
                          }}
                          className="p-1 hover:bg-orange-100 rounded"
                        >
                          <X className="w-3.5 h-3.5 text-orange-600" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Dropdown */}
                  {showUserDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {/* All Users Option */}
                      <button
                        onClick={() => {
                          setReconcileUserId('');
                          setUserSearchQuery('');
                          setShowUserDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-slate-50 border-b border-slate-200"
                      >
                        <div className="font-medium text-slate-900">All Users</div>
                        <div className="text-xs text-slate-500">Run reconciliation for everyone</div>
                      </button>
                      
                      {/* Filtered Users */}
                      {users
                        .filter(u => {
                          if (!userSearchQuery) return true;
                          const query = userSearchQuery.toLowerCase();
                          return u.email.toLowerCase().includes(query) || 
                                 u.name?.toLowerCase().includes(query);
                        })
                        .map(u => (
                          <button
                            key={u.id}
                            onClick={() => {
                              setReconcileUserId(u.id);
                              setUserSearchQuery(u.email);
                              setShowUserDropdown(false);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-slate-900 text-sm">{u.email}</div>
                                {u.name && (
                                  <div className="text-xs text-slate-500">{u.name}</div>
                                )}
                              </div>
                              <div className="text-xs text-slate-400">
                                {u.credits} credits
                              </div>
                            </div>
                          </button>
                        ))
                      }
                      
                      {users.filter(u => {
                        if (!userSearchQuery) return true;
                        const query = userSearchQuery.toLowerCase();
                        return u.email.toLowerCase().includes(query) || 
                               u.name?.toLowerCase().includes(query);
                      }).length === 0 && (
                        <div className="px-4 py-6 text-center text-sm text-slate-500">
                          No users found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoFix}
                      onChange={(e) => setAutoFix(e.target.checked)}
                      className="w-5 h-5 text-orange-600 border-slate-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm font-medium text-slate-700">
                      Auto-Fix Discrepancies
                      <span className="block text-xs text-slate-500 mt-0.5">
                        Automatically add missing credits
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <button
                onClick={handleReconcile}
                disabled={reconcileLoading}
                className="w-full px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-lg hover:from-orange-700 hover:to-red-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {reconcileLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Reconciling...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5" />
                    Run Reconciliation
                  </>
                )}
              </button>
            </div>

            {/* Reconciliation Report */}
            {reconcileReport && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Reconciliation Report
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    {new Date(reconcileReport.startedAt).toLocaleString()} • Duration: {reconcileReport.durationMs}ms
                  </p>
                </div>

                {/* Summary Cards */}
                <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-sm text-blue-700 mb-1">Users Checked</div>
                    <div className="text-2xl font-bold text-blue-900">{reconcileReport.usersChecked}</div>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="text-sm text-orange-700 mb-1">Discrepancies</div>
                    <div className="text-2xl font-bold text-orange-900">{reconcileReport.totalDiscrepancies}</div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-sm text-green-700 mb-1">Fixed</div>
                    <div className="text-2xl font-bold text-green-900">{reconcileReport.fixedDiscrepancies}</div>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="text-sm text-purple-700 mb-1">Credits Added</div>
                    <div className="text-2xl font-bold text-purple-900">+{reconcileReport.totalCreditsAdded}</div>
                  </div>
                </div>

                {/* Detailed Results */}
                {reconcileReport.users.length > 0 && (
                  <div className="px-6 pb-6">
                    <h4 className="text-md font-semibold text-slate-900 mb-3">Affected Users</h4>
                    <div className="space-y-3">
                      {reconcileReport.users.map((userReport: any, index: number) => (
                        <div key={index} className={`border rounded-lg p-4 ${
                          userReport.error ? 'border-red-200 bg-red-50' :
                          userReport.fixed ? 'border-green-200 bg-green-50' :
                          'border-orange-200 bg-orange-50'
                        }`}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="font-medium text-slate-900">{userReport.email}</div>
                              <div className="text-xs text-slate-500 font-mono">{userReport.userId}</div>
                            </div>
                            {userReport.fixed && (
                              <span className="px-2 py-1 bg-green-600 text-white text-xs font-medium rounded">
                                FIXED
                              </span>
                            )}
                            {userReport.error && (
                              <span className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded">
                                ERROR
                              </span>
                            )}
                          </div>
                          
                          {userReport.error ? (
                            <div className="text-sm text-red-700">{userReport.error}</div>
                          ) : (
                            <>
                              <div className="grid grid-cols-3 gap-4 mb-3">
                                <div>
                                  <div className="text-xs text-slate-600">Discrepancy</div>
                                  <div className="text-lg font-bold text-orange-700">
                                    {userReport.discrepancy > 0 ? '+' : ''}{userReport.discrepancy}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs text-slate-600">Stripe Total</div>
                                  <div className="text-lg font-bold text-slate-900">{userReport.stripeTotalCredits}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-slate-600">DB Balance</div>
                                  <div className="text-lg font-bold text-slate-700">{userReport.dbBalance}</div>
                                </div>
                              </div>
                              
                              {userReport.missingPayments && userReport.missingPayments.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-slate-200">
                                  <div className="text-xs font-medium text-slate-700 mb-2">Missing Payments:</div>
                                  <div className="space-y-1">
                                    {userReport.missingPayments.map((payment: any, pIndex: number) => (
                                      <div key={pIndex} className="text-xs text-slate-600 flex justify-between">
                                        <span>{payment.packageId} - {payment.credits} credits</span>
                                        <span className="font-mono">${payment.amount}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Promo Codes Tab */}
        {!loading && activeTab === 'promo' && (
          <div className="space-y-6">
            {/* Create Promo Form */}
            <PromoCodeForm
              onSubmit={handleCreatePromo}
              presets={presets}
              onPresetChange={applyPreset}
              code={newPromoCode}
              setCode={setNewPromoCode}
              credits={newPromoCredits}
              setCredits={setNewPromoCredits}
              description={newPromoDescription}
              setDescription={setNewPromoDescription}
              showAdvanced={showAdvanced}
              setShowAdvanced={setShowAdvanced}
              selectedPreset={selectedPreset}
              setSelectedPreset={setSelectedPreset}
              type={newPromoType}
              setType={setNewPromoType}
              category={newPromoCategory}
              setCategory={setNewPromoCategory}
              maxUses={newPromoMaxUses}
              setMaxUses={setNewPromoMaxUses}
              maxUsesPerUser={newPromoMaxUsesPerUser}
              setMaxUsesPerUser={setNewPromoMaxUsesPerUser}
              dailyLimit={newPromoDailyLimit}
              setDailyLimit={setNewPromoDailyLimit}
              startsAt={newPromoStartsAt}
              setStartsAt={setNewPromoStartsAt}
              expiresAt={newPromoExpiresAt}
              setExpiresAt={setNewPromoExpiresAt}
              campaignName={newPromoCampaignName}
              setCampaignName={setNewPromoCampaignName}
              restrictionType={restrictionType}
              setRestrictionType={setRestrictionType}
              newUserDays={newUserDays}
              setNewUserDays={setNewUserDays}
              inactiveDays={inactiveDays}
              setInactiveDays={setInactiveDays}
              minProjects={minProjects}
              setMinProjects={setMinProjects}
              maxProjects={maxProjects}
              setMaxProjects={setMaxProjects}
              emailVerified={emailVerified}
              setEmailVerified={setEmailVerified}
              neverPurchased={neverPurchased}
              setNeverPurchased={setNeverPurchased}
              isRecurring={isRecurring}
              setIsRecurring={setIsRecurring}
              recurringInterval={recurringInterval}
              setRecurringInterval={setRecurringInterval}
              recurringCount={recurringCount}
              setRecurringCount={setRecurringCount}
            />

            {/* Promo Codes List */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">
                    <Gift className="w-5 h-5 inline mr-2" />
                    Promo Codes ({promoCodes.length})
                  </h3>
                  {promoCodes.length > 0 && (
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <span>
                        <BarChart3 className="w-4 h-4 inline mr-1" />
                        Total Used: {promoCodes.reduce((sum, p) => sum + (p.usedCount || 0), 0)}
                      </span>
                      <span>
                        <Users className="w-4 h-4 inline mr-1" />
                        Unique Users: {promoCodes.reduce((sum, p) => sum + (p.uniqueUsers || 0), 0)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Credits</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Restrictions</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Usage</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Analytics</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {promoCodes.map((promo) => {
                      const restrictionSummary = (() => {
                        const r = promo.userRestrictions;
                        if (!r || r.type === 'all') return 'All users';
                        if (r.type === 'new_users') return `New (${r.newUserDays || 30}d)`;
                        if (r.type === 'inactive_users') return `Inactive (${r.inactiveDays || 60}d)`;
                        if (r.type === 'verified_only') return 'Verified only';
                        if (r.type === 'custom') {
                          const rules = [];
                          if (r.newUserDays) rules.push(`New ${r.newUserDays}d`);
                          if (r.minProjects) rules.push(`Min ${r.minProjects} proj`);
                          if (r.neverPurchased) rules.push('No purchase');
                          return rules.join(', ') || 'Custom';
                        }
                        return 'N/A';
                      })();

                      const typeColor = {
                        public: 'bg-blue-100 text-blue-800',
                        targeted: 'bg-purple-100 text-purple-800',
                        unique: 'bg-amber-100 text-amber-800',
                        recurring: 'bg-emerald-100 text-emerald-800',
                      }[promo.type] || 'bg-slate-100 text-slate-800';

                      return (
                        <motion.tr
                          key={promo.code}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="hover:bg-slate-50"
                        >
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-mono font-medium text-slate-900">{promo.code}</div>
                              {promo.description && (
                                <div className="text-xs text-slate-500 mt-0.5">{promo.description}</div>
                              )}
                              {promo.campaignName && (
                                <div className="text-xs text-emerald-600 mt-0.5">
                                  <Target className="w-3 h-3 inline mr-0.5" />
                                  {promo.campaignName}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium inline-block w-fit ${typeColor}`}>
                                {promo.type || 'public'}
                              </span>
                              {promo.category && (
                                <span className="text-xs text-slate-500">{promo.category}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm font-semibold text-slate-900">{promo.credits}</td>
                          <td className="px-6 py-4">
                            <div className="text-xs text-slate-600">
                              {restrictionSummary}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <div className="font-medium text-slate-900">
                                {promo.usedCount || 0} {promo.maxUses ? `/ ${promo.maxUses}` : '/ ∞'}
                              </div>
                              {promo.maxUsesPerUser && (
                                <div className="text-xs text-slate-500">Max/user: {promo.maxUsesPerUser}</div>
                              )}
                              {promo.dailyLimit && (
                                <div className="text-xs text-slate-500">Daily: {promo.dailyLimit}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-xs text-slate-600">
                              <div>Users: {promo.uniqueUsers || 0}</div>
                              <div>Credits: {promo.totalCreditsGiven || 0}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium inline-block w-fit ${
                                promo.isActive 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {promo.isActive ? 'Active' : 'Inactive'}
                              </span>
                              {promo.expiresAt && (
                                <div className="text-xs text-slate-500">
                                  Exp: {new Date(promo.expiresAt).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleTogglePromo(promo.code)}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                title={promo.isActive ? 'Deactivate' : 'Activate'}
                              >
                                {promo.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => handleDeletePromo(promo.code)}
                                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
                {promoCodes.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No promo codes yet. Create your first one above!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}